import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-pagespeed-optimieren";
const TITLE = "Shopify Pagespeed optimieren: schneller verkaufen";
const DESCRIPTION =
  "Shopify Pagespeed optimieren ohne Theme-Neubau: Apps entrümpeln, Bilder und Fonts richtig ausliefern und den Umsatzeffekt sauber messen.";

const faqs = [
  {
    q: "Wie stark beeinflusst Ladezeit die Conversion-Rate?",
    a: "Als Faustregel gilt: Jede zusätzliche Sekunde bis zur Interaktivität kostet mobil spürbar Umsatz. Der Effekt ist am größten zwischen einer und drei Sekunden, danach flacht die Kurve ab. Wichtiger als der Lighthouse-Score sind die Felddaten echter Nutzer.",
  },
  {
    q: "Brauche ich ein neues Theme, um schneller zu werden?",
    a: "In den meisten Fällen nicht. Der Großteil der Ladezeit stammt aus Apps, Tracking-Skripten und unoptimierten Bildern, nicht aus dem Theme selbst. Ein Neubau ist erst sinnvoll, wenn das Theme älter als die Online-Store-2.0-Struktur ist.",
  },
  {
    q: "Welcher Lighthouse-Score ist gut genug?",
    a: "Ein mobiler Score über 50 bei sauberen Core Web Vitals reicht für die meisten Shops. Wer Punkte jagt statt Ladezeit, optimiert an der Kasse vorbei.",
  },
  {
    q: "Wie viele Apps sind zu viele?",
    a: "Es gibt keine Zahl, nur eine Frage: Trägt die App messbar zum Umsatz bei? Jede App, die das nicht kann, ist ein Kandidat zum Entfernen, inklusive der Skriptreste, die nach der Deinstallation im Theme bleiben.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-pagespeed-optimieren")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Pagespeed"
      title="Shopify Pagespeed optimieren: die Reihenfolge, die wirklich Zeit spart"
      intro="Geschwindigkeit ist kein Selbstzweck. Sie ist der günstigste Weg, Kaufabsicht nicht zu verlieren, bevor sie überhaupt sichtbar wird, besonders mobil, wo die Geduld am kürzesten ist."
      source="ratgeber-pagespeed"
      sections={[
        {
          h: "Erst messen, was der Kunde erlebt",
          p: [
            "Lighthouse misst ein Labor. Entscheidend sind die Felddaten Ihrer echten Besucher: Largest Contentful Paint, Interaction to Next Paint und Cumulative Layout Shift, getrennt nach Mobile und Desktop.",
            "Miss außerdem auf den Seiten, die Umsatz bringen, Startseite, meistbesuchte Kollektion, Top-Produktseite und Warenkorb. Ein schneller Blogartikel hilft niemandem an der Kasse.",
          ],
        },
        {
          h: "Apps und Skripte: der größte Hebel",
          p: [
            "In fast jedem gewachsenen Shop liegt die Hälfte der Ladezeit in Drittanbieter-Skripten. Reviews, Popups, Wishlists, zwei Tracking-Systeme parallel, dazu Reste deinstallierter Apps im Theme-Code.",
          ],
          list: [
            "Alle Apps auflisten und dem jeweiligen Umsatzbeitrag gegenüberstellen",
            "Skriptreste deinstallierter Apps aus theme.liquid entfernen",
            "Tracking über einen Container bündeln statt fünf einzelne Snippets",
            "Popups erst nach der ersten Interaktion laden",
            "Chat-Widgets verzögert einbinden, nicht im kritischen Pfad",
          ],
        },
        {
          h: "Bilder und Fonts richtig ausliefern",
          p: [
            "Bilder sind der größte Byte-Block. Moderne Formate, korrekte Größenangaben im srcset und Lazy Loading für alles unterhalb des ersten Bildschirms erledigen den Großteil der Arbeit.",
            "Genau ein Bild sollte nicht lazy geladen werden: das Hero- oder Produktbild oberhalb der Falz. Es gehört vorgeladen, weil es fast immer das LCP-Element ist.",
            "Bei Schriften reichen zwei Schnitte. Lokal einbinden, font-display: swap setzen, Fallback-Metriken angleichen, das verhindert die Layout-Sprünge, die den CLS-Wert ruinieren.",
          ],
        },
        {
          h: "Layout-Stabilität schlägt Millisekunden",
          p: [
            "Ein Shop, der in 1,8 Sekunden lädt, aber beim Laden dreimal springt, fühlt sich langsamer an als einer mit 2,4 Sekunden ohne Sprünge. Feste Höhen für Banner, Bildcontainer und Ankündigungsleisten kosten nichts und wirken sofort.",
          ],
        },
        {
          h: "Den Umsatzeffekt belegen",
          p: [
            "Nach dem Eingriff vergleichen Sie nicht Scores, sondern Kennzahlen: Kaufabschlussrate mobil, Umsatz pro Session und Absprungrate der Einstiegsseiten, über mindestens zwei vergleichbare Wochen.",
            "So wird aus einer technischen Aufräumaktion eine belegbare Investition und keine Geschmacksfrage.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-conversion-optimierung", label: "Conversion-Leitfaden" },
        { to: "/ratgeber/shopify-mobile-optimierung", label: "Mobile optimieren" },
        { to: "/kontakt", label: "Store prüfen lassen" },
      ]}
    />
  );
}
